import React, { useState } from "react"
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Collapse,
  Badge,
} from "reactstrap"
import moment from "moment"
import { UpdateCPSRequest, UploadCPSAttachment } from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"

const CPSSampleProduction = ({ entry, myUser, statusIndex, reload }) => {
  const setLoading = useLoading()
  const [isOpen, setIsOpen] = useState(false)
  const [sampleReport, setSampleReport] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [sampleReturnTrackingNumber, setSampleReturnTrackingNumber] =
    useState("")
  const [sampleReturnShippingService, setSampleReturnShippingService] =
    useState("")

  // Determine if this step is active based on entry statusIndex
  const isActive = entry?.statusIndex === 6
  const isCompleted = entry?.statusIndex > 6
  const isCPSLab = myUser?.company?.id === entry?.cpsLabID
  const isWarehouseUser = myUser?.cpsRole === "Warehouse"
  const canUpload = isActive && !isCompleted && isCPSLab && !isWarehouseUser

  const handleUploadReport = async e => {
    e.preventDefault()
    if (!sampleReport) return

    setIsUploading(true)
    setLoading(true)

    try {
      // Upload the sample report file
      const formData = new FormData()
      formData.append("file", sampleReport)
      formData.append("id", entry?.id)
      const uploadResult = await UploadCPSAttachment(formData)

      // Update the request to move to next status
      const updatedEntry = {
        ...entry,
        sampleReportURL: uploadResult?.url,
        statusIndex: 7, // Move to Sample Approval
        // Reset decline fields when new sample report is uploaded
        sampleDeclined: false,
        sampleDeclinedDate: null,
        sampleDeclineReason: null,
        // Add return tracking information
        sampleReturnTrackingNumber: sampleReturnTrackingNumber || null,
        sampleReturnShippingService: sampleReturnShippingService || null,
      }
      await UpdateCPSRequest(updatedEntry)

      setIsUploading(false)
      setLoading(false)
      setIsOpen(false)
      setSampleReport(null)

      // Small delay to ensure backend has processed the update
      setTimeout(() => {
        reload()
      }, 500)
    } catch (error) {
      console.error("Error uploading sample report:", error)
      setIsUploading(false)
      setLoading(false)
      alert("Error uploading sample report. Please try again.")
    }
  }

  return (
    <li className={"event-list " + (isActive ? "active" : "")}>
      <div
        className={"event-timeline-dot " + (isActive ? "bx-fade-right" : "")}
      >
        <i className="bx bx-right-arrow-circle font-size-18"></i>
      </div>
      <div className="d-flex">
        <div className="flex-shrink-0 me-3">
          <h5 className="font-size-14">
            {isCompleted
              ? moment(entry?.sampleReportUploaded).format("DD.MM.YYYY")
              : ""}{" "}
            <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
          </h5>
        </div>
        <div className="flex-grow-1">
          <div>
            Sample Production{" "}
            {isActive && !isCompleted
              ? ` - producing ${entry?.sampleQuantity || 25} sample pieces`
              : isCompleted
              ? ` - ${entry?.sampleQuantity || 25} sample pieces completed`
              : ""}
          </div>
        </div>
      </div>

      {isActive && canUpload && (
        <div className="mt-3">
          <Button
            color="info"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            className="mb-2"
          >
            <i className="bx bx-upload font-size-12 me-1"></i>
            Upload Sample Report
          </Button>

          <Collapse isOpen={isOpen}>
            <Form onSubmit={handleUploadReport}>
              <FormGroup>
                <Label for="sampleReport">Sample Report</Label>
                <Input
                  type="file"
                  onChange={e => setSampleReport(e.target.files[0])}
                  accept=".pdf,.doc,.docx"
                  disabled={isUploading}
                />
              </FormGroup>

              <FormGroup>
                <Label for="sampleReturnTrackingNumber">
                  Sample Return Tracking Number
                </Label>
                <Input
                  type="text"
                  id="sampleReturnTrackingNumber"
                  value={sampleReturnTrackingNumber}
                  onChange={e => setSampleReturnTrackingNumber(e.target.value)}
                  placeholder="e.g., 1Z999AA1234567890"
                  disabled={isUploading}
                />
                <div className="form-text text-muted">
                  Tracking number for returning samples to customer
                </div>
              </FormGroup>

              <FormGroup>
                <Label for="sampleReturnShippingService">
                  Sample Return Shipping Service
                </Label>
                <Input
                  type="text"
                  id="sampleReturnShippingService"
                  value={sampleReturnShippingService}
                  onChange={e => setSampleReturnShippingService(e.target.value)}
                  placeholder="e.g., DHL, FedEx, UPS"
                  disabled={isUploading}
                />
                <div className="form-text text-muted">
                  Shipping service used for returning samples
                </div>
              </FormGroup>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  color="success"
                  size="sm"
                  disabled={!sampleReport || isUploading}
                >
                  {isUploading ? "Uploading..." : "Upload Report"}
                </Button>
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false)
                    setSampleReport(null)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Collapse>
        </div>
      )}

      {isActive && isCPSLab && isWarehouseUser && (
        <div className="mt-3">
          <div className="alert alert-warning mb-0">
            <i className="bx bx-info-circle me-2"></i>
            Only CPS Admin users can upload or change sample reports. This step
            is read-only for warehouse users.
          </div>
        </div>
      )}

      {isCompleted && (
        <div className="mt-3">
          <div className="d-flex align-items-center">
            <div className="me-2">
              <Badge color="success">Completed</Badge>
            </div>
            <div>
              <small className="text-muted">
                Sample report uploaded on{" "}
                {moment(entry?.sampleReportUploaded).format("DD.MM.YYYY")}
              </small>
            </div>
          </div>
          {entry?.sampleReportURL && (
            <div className="mt-2">
              <a
                href={entry.sampleReportURL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                <i className="bx bx-download me-1"></i>
                Download Sample Report
              </a>
            </div>
          )}

          {/* Sample Return Tracking Information */}
          {(entry?.sampleReturnTrackingNumber ||
            entry?.sampleReturnShippingService) && (
            <div className="d-flex align-items-center mt-3">
              <div style={{ width: "45px" }}>
                <div className="avatar-sm">
                  <span className="avatar-title rounded-circle bg-success bg-soft text-success font-size-24">
                    <i className="bx bx-package" />
                  </span>
                </div>
              </div>
              <div className="ms-4">
                <h5 className="font-size-14 mb-1">Sample Return Shipped</h5>
                {entry?.sampleReturnTrackingNumber && (
                  <div className="mt-1">
                    <small className="text-muted">
                      <i className="bx bx-package me-1"></i>
                      Tracking: {entry.sampleReturnTrackingNumber}
                    </small>
                  </div>
                )}
                {entry?.sampleReturnShippingService && (
                  <div>
                    <small className="text-muted">
                      <i className="bx bx-truck me-1"></i>
                      Service: {entry.sampleReturnShippingService}
                    </small>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <p className="mb-0 mt-2">
        <i className="bx bx-time-five font-size-13 align-middle me-1"></i>
        <span className="font-size-13">
          {isCompleted
            ? "Sample completed"
            : isActive
            ? "In progress"
            : "Not started"}
        </span>
      </p>
    </li>
  )
}

export default CPSSampleProduction
